import { QuestionGroupGuidelineCollapseExpand } from '@doltech/ui/lib/figma/QuestionGroupGuideline/QuestionGroupGuidelineCollapseExpand';
import { QuestionTypeEnum, SkillEnum } from '@doltech/utils/lib/constants';
import * as mobx from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ERROR_NOT_SUPPORTED } from '../../question-data-model/constants';
import { CompletionNoHintAnswer } from '../../student-do-test-questions/CompletionNoHintAnswer';
import { CompletionWithHintAnswer } from '../../student-do-test-questions/CompletionWithHintAnswer';
import { DiagramLabelCompletionAnswer } from '../../student-do-test-questions/DiagramLabelCompletionAnswer';
import { DragInAnswer } from '../../student-do-test-questions/DragInAnswer';
import { DragOutAnswer } from '../../student-do-test-questions/DragOutAnswer';
import { FlowChartAnswer } from '../../student-do-test-questions/FlowChartAnswer';
import { FlowChartComplexAnswer } from '../../student-do-test-questions/FlowChartComplexAnswer';
import { MatchingEndingAnswer } from '../../student-do-test-questions/MatchingEndingAnswer';
import { MatchingFeatureNameAnswer } from '../../student-do-test-questions/MatchingFeatureNameAnswer';
import { MatchingHeadingAnswer } from '../../student-do-test-questions/MatchingHeadingAnswer';
import { MatchingParagraphAnswer } from '../../student-do-test-questions/MatchingParagraphAnswer';
import { MultipleAnswer } from '../../student-do-test-questions/MultipleAnswer';
import { SentenceComplete } from '../../student-do-test-questions/SentenceComplete';
import { ShortAnswer } from '../../student-do-test-questions/ShortAnswer';
import { SingleAnswer } from '../../student-do-test-questions/SingleAnswer';
import { TableCompletion } from '../../student-do-test-questions/TableCompletion';
import { TFNAnswer } from '../../student-do-test-questions/TFNAnswer';
import { YNNAnswer } from '../../student-do-test-questions/YNNAnswer';
import { useTestSubmissionStore } from '../model/test-submissions.hook';

interface TestContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const GroupQuestionsWrapper = styled.div`
  .header-container {
    margin-bottom: 8px;
  }
  .collapse-expand-toggle-wrapper {
    position: absolute;
    top: 4px;
    right: 4px;
    button:hover {
      background-color: unset !important;
    }
  }
`;

const Main = styled.div`
  padding: 16px 24px;
  display: grid;
  .dol-question {
    margin-bottom: 12px;
  }

  .hide {
    display: none;
  }

  > div {
    margin-bottom: 12px;
  }
`;

export interface QuestionProps {
  item: any;
  currentSection: any;
}

export const Question = observer((props: QuestionProps) => {
  const { item, currentSection } = props;
  const { uuid, type, no } = item;

  const defaultProps = {
    id: `question-no-${no}`,
    key: uuid,
    currentSection,
  };

  switch (type) {
    case QuestionTypeEnum.SINGLE_ANSWER:
      return <SingleAnswer {...defaultProps} data={item} />;

    case QuestionTypeEnum.MULTIPLE_ANSWER: {
      return <MultipleAnswer {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.TFN_ANSWER: {
      return <TFNAnswer {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.YNN_ANSWER: {
      return <YNNAnswer {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_PARAGRAPH: {
      return <MatchingParagraphAnswer {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_HEADING: {
      return <MatchingHeadingAnswer {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_ENDING: {
      return <MatchingEndingAnswer {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_FEATURE_NAME: {
      return <MatchingFeatureNameAnswer {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.SENTENCE_COMPLETE:
      return <SentenceComplete {...defaultProps} data={item} />;
    case QuestionTypeEnum.SHORT_ANSWER:
      return <ShortAnswer {...defaultProps} data={item} />;
    case QuestionTypeEnum.NOTE_COMPLETION_NO_HINT:
      return <CompletionNoHintAnswer {...defaultProps} data={item} />;
    case QuestionTypeEnum.FLOWCHART_ANSWER:
      return <FlowChartAnswer {...defaultProps} data={item} />;
    case QuestionTypeEnum.NOTE_COMPLETION_WITH_HINT:
      return <CompletionWithHintAnswer {...defaultProps} data={item} />;
    case QuestionTypeEnum.TABLE_COMPLETION:
      return <TableCompletion {...defaultProps} data={item} />;
    case QuestionTypeEnum.DIAGRAM_LABEL_COMPLETION:
      return <DiagramLabelCompletionAnswer {...defaultProps} data={item} />;
    case QuestionTypeEnum.DRAG_IN:
      return <DragInAnswer {...defaultProps} data={item} />;
    case QuestionTypeEnum.DRAG_OUT:
      return <DragOutAnswer {...defaultProps} data={item} />;
    case QuestionTypeEnum.FLOW_CHART_COMPLEX:
      return <FlowChartComplexAnswer {...defaultProps} data={item} />;
    default:
      // default return to know if api return the question type that was not supported
      return (
        <div>
          {type} {ERROR_NOT_SUPPORTED}
        </div>
      );
  }
});

interface GroupQuestionsProps {
  group: any;
  currentSection: any;
}

export const GroupQuestionsListening = React.memo(
  ({ group, currentSection }: GroupQuestionsProps) => {
    const questionGroupHeader = group.getQuestionGroupsFromNoToNo();
    const { content = [], enable = false } = group.compactGuideline ?? {};

    return (
      <GroupQuestionsWrapper>
        <div className="header-container">
          <QuestionGroupGuidelineCollapseExpand
            key={group.id}
            title={questionGroupHeader}
            content={mobx.toJS(group.guidelineContent)}
            compactContent={mobx.toJS(content)}
            compactEnable={enable}
            initialExpandValue
          />
        </div>
        <div className="test-content-container">
          {group?.questions.map((item) => {
            return <Question key={item.uuid} item={item} currentSection={currentSection} />;
          })}
        </div>
      </GroupQuestionsWrapper>
    );
  }
);

export const GroupQuestions = React.memo(({ group, currentSection }: GroupQuestionsProps) => {
  return (
    <GroupQuestionsWrapper>
      <div className="header-container"></div>
      <div className="test-content-container">
        {group?.map((item) => {
          const { compactGuideline, guideline, questions } = item;
          const { 0: firstQuestion, length, [length - 1]: lastQuestion } = questions;
          const leftNum = firstQuestion.no;
          const rightNum = lastQuestion.no + (lastQuestion.questionCount - 1);
          let title = '';

          if (leftNum === rightNum) {
            title = `Question ${leftNum}`;
          }
          title = `Question ${leftNum} - ${rightNum}`;

          return (
            <React.Fragment key={item.id}>
              <QuestionGroupGuidelineCollapseExpand
                key={group.id}
                title={title}
                content={mobx.toJS(guideline?.content)}
                compactContent={mobx.toJS(compactGuideline?.content)}
                compactEnable={true}
                initialExpandValue
              />
              {questions?.map((eachQuestion) => {
                return (
                  <Question
                    key={eachQuestion.uuid}
                    item={eachQuestion}
                    currentSection={currentSection}
                  />
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </GroupQuestionsWrapper>
  );
});

export const TestContent = observer((props: TestContentProps) => {
  const store = useTestSubmissionStore();
  const currentSection = store.testSections.find(
    (_section, sectionIndex) => sectionIndex === store.currentSectionIndex
  );
  if (Boolean(currentSection) === false) return null;

  return (
    <Main {...props}>
      {store.skill === SkillEnum.READING ? (
        <GroupQuestions currentSection={currentSection} group={currentSection.questionGroups} />
      ) : (
        currentSection.questionGroups.map((group) => {
          return (
            <div key={group.id}>
              <GroupQuestionsListening currentSection={currentSection} group={group} />
            </div>
          );
        })
      )}
    </Main>
  );
});
